import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

export type SlaCreditStatus = 'draft' | 'approved' | 'invoiced' | 'rejected'
export type MeasurementPeriod = 'monthly' | 'quarterly' | 'yearly'

export interface CreditTier {
  thresholdPercentBp: number
  creditPercentBp: number
}

export interface SlaProfile {
  id: string
  tenantId: string
  name: string
  description: string | null
  guaranteedAvailabilityBp: number
  measurementPeriod: MeasurementPeriod
  reactionTimeMinutes: number | null
  resolutionTimeMinutes: number | null
  creditTiers: CreditTier[]
  active: boolean
  createdAt: string
  updatedAt: string
  assignedContracts: number
}

export interface SlaCreditRow {
  credit: {
    id: string
    tenantId: string
    contractId: string
    profileId: string | null
    periodStart: string
    periodEnd: string
    measuredAvailabilityBp: number
    downtimeMinutes: number
    creditPercentBp: number
    creditAmountCents: number
    currency: string
    status: SlaCreditStatus
    notes: string | null
    tanssTicketIds: string | null
    createdAt: string
    approvedAt: string | null
    invoicedAt: string | null
    sapCreditMemoEntry: number | null
  }
  contract: { id: string; contractNumber: string; name: string | null } | null
  profile: { id: string; name: string } | null
}

export interface SlaCreditPreview {
  measuredAvailabilityBp: number
  creditPercentBp: number
  creditAmountCents: number
  monthlyContractValueCents: number
  periodMinutes: number
  matchedTier: CreditTier | null
  meetsSla: boolean
}

export interface CreateSlaProfileInput {
  name: string
  description?: string | null
  guaranteedAvailabilityBp?: number
  measurementPeriod?: MeasurementPeriod
  reactionTimeMinutes?: number | null
  resolutionTimeMinutes?: number | null
  creditTiers?: CreditTier[]
  active?: boolean
}

export interface CreateSlaCreditInput {
  contractId: string
  profileId?: string | null
  periodStart: string
  periodEnd: string
  downtimeMinutes: number
  measuredAvailabilityBp?: number
  notes?: string | null
  tanssTicketIds?: string | null
}

// ─── Profile ────────────────────────────────────────────────────────────────

export function useSlaProfiles() {
  return useQuery<SlaProfile[]>({
    queryKey: ['sla', 'profiles'],
    queryFn: () => apiClient.get('/sla/profiles').then((r) => r.data),
  })
}

export function useCreateSlaProfile() {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, CreateSlaProfileInput>({
    mutationFn: (input) => apiClient.post('/sla/profiles', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useUpdateSlaProfile(id: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, Partial<CreateSlaProfileInput>>({
    mutationFn: (patch) => apiClient.patch(`/sla/profiles/${id}`, patch).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useDeleteSlaProfile() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/sla/profiles/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

// ─── Credits ────────────────────────────────────────────────────────────────

export function useSlaCredits(filter: { contractId?: string; status?: SlaCreditStatus } = {}) {
  const params: Record<string, string> = {}
  if (filter.contractId) params['contractId'] = filter.contractId
  if (filter.status) params['status'] = filter.status
  return useQuery<SlaCreditRow[]>({
    queryKey: ['sla', 'credits', filter],
    queryFn: () => apiClient.get('/sla/credits', { params }).then((r) => r.data),
  })
}

export function usePreviewSlaCredit() {
  return useMutation<SlaCreditPreview, Error, CreateSlaCreditInput>({
    mutationFn: (input) => apiClient.post('/sla/credits/preview', input).then((r) => r.data),
  })
}

export function useCreateSlaCredit() {
  const qc = useQueryClient()
  return useMutation<{ id: string; preview: SlaCreditPreview }, Error, CreateSlaCreditInput>({
    mutationFn: (input) => apiClient.post('/sla/credits', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useApproveSlaCredit() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.post(`/sla/credits/${id}/approve`, {}).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useRejectSlaCredit() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.post(`/sla/credits/${id}/reject`, {}).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useMarkSlaCreditInvoiced() {
  const qc = useQueryClient()
  return useMutation<void, Error, { id: string; sapDocEntry?: number | null }>({
    mutationFn: ({ id, sapDocEntry }) =>
      apiClient.post(`/sla/credits/${id}/mark-invoiced`, { sapDocEntry }).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}

export function useDeleteSlaCredit() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/sla/credits/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sla'] }),
  })
}
